/**
 * Service pour synchroniser les custom claims Firebase Auth
 * Appelé une fois au moment de la connexion pour mettre à jour les claims
 * (isSuperAdmin, customerId) dans le token Firebase Auth
 */

import { auth } from '@/shared/config/firebase';

// URL de base des Cloud Functions
const FUNCTIONS_BASE_URL = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL ||
    'https://europe-west1-cashlive-develop.cloudfunctions.net';

interface SyncClaimsResponse {
    success: boolean;
    claims?: {
        isSuperAdmin: boolean;
        customerId?: string;
    };
    message?: string;
    error?: string;
}

interface ImpersonateResponse {
    success: boolean;
    customToken?: string;
    targetUser?: {
        uid: string;
        email?: string;
        displayName?: string;
    };
    error?: string;
}

interface StopImpersonationResponse {
    success: boolean;
    customToken?: string;
    error?: string;
}

export interface ImpersonationInfo {
    isImpersonating: boolean;
    impersonatedByEmail?: string;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('Utilisateur non connecté');
    }
    const token = await user.getIdToken();
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    };
}

/**
 * Synchronise les custom claims d'un utilisateur
 * Appelé après la connexion pour mettre à jour le token avec les permissions
 * 
 * @param uid - Firebase Auth UID de l'utilisateur
 * @returns Les claims mis à jour ou null en cas d'erreur
 */
export async function syncUserClaims(uid: string): Promise<SyncClaimsResponse | null> {
    try {
        console.log(`🔐 Synchronisation des claims pour UID: ${uid}`);

        const response = await fetch(`${FUNCTIONS_BASE_URL}/syncUserClaims`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('❌ Erreur syncUserClaims:', errorData);
            return null;
        }

        const data: SyncClaimsResponse = await response.json();

        if (data.success) {
            console.log('✅ Claims synchronisés:', data.claims);
        } else {
            console.warn('⚠️ Échec synchronisation claims:', data.error);
        }

        return data;

    } catch (error) {
        console.error('❌ Erreur lors de la synchronisation des claims:', error);
        return null;
    }
}

/**
 * Force le rafraîchissement du token Firebase Auth
 * Appeler après syncUserClaims pour que les nouveaux claims soient disponibles
 * 
 * @param user - L'utilisateur Firebase Auth actuel
 */
export async function refreshAuthToken(user: { getIdToken: (forceRefresh?: boolean) => Promise<string> }): Promise<string | null> {
    try {
        console.log('🔄 Rafraîchissement du token Firebase Auth...');

        // Forcer le rafraîchissement du token pour obtenir les nouveaux claims
        const newToken = await user.getIdToken(true);

        console.log('✅ Token rafraîchi avec les nouveaux claims');
        return newToken;

    } catch (error) {
        console.error('❌ Erreur lors du rafraîchissement du token:', error);
        return null;
    }
}

/**
 * Demande un custom token pour se connecter en tant qu'un autre utilisateur
 */
export async function requestImpersonationToken(email: string): Promise<ImpersonateResponse> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${FUNCTIONS_BASE_URL}/impersonateUser`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });

    const data: ImpersonateResponse = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de l\'impersonation');
    }
    return data;
}

/**
 * Termine l'impersonation et récupère un token pour revenir au compte super admin
 */
export async function requestStopImpersonationToken(): Promise<StopImpersonationResponse> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${FUNCTIONS_BASE_URL}/stopImpersonation`, {
        method: 'POST',
        headers,
    });

    const data: StopImpersonationResponse = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la fin d\'impersonation');
    }
    return data;
}

/**
 * Lit les claims d'impersonation depuis le token Firebase Auth courant
 */
export async function getImpersonationInfo(): Promise<ImpersonationInfo> {
    const user = auth.currentUser;
    if (!user) {
        return { isImpersonating: false };
    }

    const tokenResult = await user.getIdTokenResult();
    const impersonatedBy = tokenResult.claims.impersonatedBy;

    return {
        isImpersonating: !!impersonatedBy,
        impersonatedByEmail: tokenResult.claims.impersonatedByEmail as string | undefined,
    };
}
